import { useQuery } from '@tanstack/react-query';

import * as reportsApi from './reports.api';

import type { LeadPipelineKind, LeadPivotDimension } from '@utazzo/shared';

const QK = {
  agentTracker: (from: string, to: string) => ['reports', 'agent-tracker', from, to] as const,
  performanceDay: (date: string) => ['reports', 'performance-day', date] as const,
  pivot: (
    from: string,
    to: string,
    dimension: LeadPivotDimension,
    carryForwardOnly: boolean,
    lostFromSubStatusCode?: string,
    largeOnly?: boolean,
  ) =>
    [
      'reports',
      'pivot',
      dimension,
      carryForwardOnly,
      lostFromSubStatusCode,
      largeOnly ?? false,
      from,
      to,
    ] as const,
  pipeline: (from: string, to: string, kind: LeadPipelineKind) =>
    ['reports', 'pipeline', kind, from, to] as const,
};

export const useAgentTracker = (from: string | null, to: string | null) =>
  useQuery({
    queryKey: QK.agentTracker(from ?? '', to ?? ''),
    queryFn: () => reportsApi.getAgentTracker(from!, to!),
    enabled: Boolean(from && to),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

export const usePerformanceDay = (date: string | null) =>
  useQuery({
    queryKey: QK.performanceDay(date ?? ''),
    queryFn: () => reportsApi.getPerformanceDay(date!),
    enabled: Boolean(date),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

export const usePipeline = (from: string | null, to: string | null, kind: LeadPipelineKind) =>
  useQuery({
    queryKey: QK.pipeline(from ?? '', to ?? '', kind),
    queryFn: () => reportsApi.getPipeline(from!, to!, kind),
    enabled: Boolean(from && to),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

export const usePivot = (
  from: string | null,
  to: string | null,
  dimension: LeadPivotDimension,
  carryForwardOnly = false,
  lostFromSubStatusCode?: string,
  largeOnly?: boolean,
) =>
  useQuery({
    queryKey: QK.pivot(
      from ?? '',
      to ?? '',
      dimension,
      carryForwardOnly,
      lostFromSubStatusCode,
      largeOnly,
    ),
    queryFn: () =>
      reportsApi.getPivot(
        from!,
        to!,
        dimension,
        carryForwardOnly,
        lostFromSubStatusCode,
        largeOnly,
      ),
    enabled: Boolean(from && to),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
